package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import se.mittpass.shared.api.SlotDto

// ──────── Schema view enums + constants ────────

internal enum class ViewMode(val label: String, val icon: String) {
    WEEK("Vecka", "fa-solid fa-calendar-week"),
    MONTH("Månad", "fa-solid fa-calendar"),
    YEAR("År", "fa-solid fa-calendar-days")
}


internal val DAY_NAMES_SHORT = listOf("Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön")

/**
 * Evaluates whether a shift was assigned to another employee when the current user applied for it.
 */
internal fun SlotDto.isFilledByOther(currentUserId: Long?): Boolean {
    if (currentUserId == null || assignedToUserId == null) return false
    val isAssignedToOther = assignedToUserId != currentUserId
    val userApplied = interestedUsers.any { it.id == currentUserId }
    return isAssignedToOther && userApplied
}

internal val MONTH_NAMES = listOf(
    "januari", "februari", "mars", "april", "maj", "juni",
    "juli", "augusti", "september", "oktober", "november", "december"
)

// ──────── Date helpers (JS Date based) ────────

internal fun todayLocalDate(): String {
    val d = kotlin.js.Date()
    return formatDateJs(d)
}

internal fun formatDateJs(d: kotlin.js.Date): String {
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

internal fun parseDate(dateStr: String): kotlin.js.Date =
    kotlin.js.Date("${dateStr}T12:00:00")

internal fun addDays(dateStr: String, days: Int): String {
    val d = parseDate(dateStr)
    (d.asDynamic()).setDate(d.getDate() + days)
    return formatDateJs(d)
}

internal fun addMonths(dateStr: String, months: Int): String {
    val d = parseDate(dateStr)
    (d.asDynamic()).setMonth(d.getMonth() + months)
    return formatDateJs(d)
}

internal fun addYears(dateStr: String, years: Int): String {
    val d = parseDate(dateStr)
    (d.asDynamic()).setFullYear(d.getFullYear() + years)
    return formatDateJs(d)
}

internal fun mondayOf(dateStr: String): String {
    val d = parseDate(dateStr)
    val dayOfWeek = (d.getDay() + 6) % 7
    return addDays(dateStr, -dayOfWeek)
}

internal fun firstOfMonth(dateStr: String): String {
    val d = parseDate(dateStr)
    return "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-01"
}

internal fun monthGridRange(anchor: String): Pair<String, String> {
    val first = firstOfMonth(anchor)
    val gridStart = mondayOf(first)
    val gridEnd = addDays(gridStart, 6 * 7 - 1)
    return gridStart to gridEnd
}

internal fun daysBetween(from: String, to: String): Int {
    val d1 = parseDate(from)
    val d2 = parseDate(to)
    return ((d2.getTime() - d1.getTime()) / (1000.0 * 60 * 60 * 24)).toInt()
}

internal fun weekNumber(dateStr: String): Int {
    val d = parseDate(dateStr)
    val target = kotlin.js.Date(d.getTime())
    (target.asDynamic()).setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7))
    val jan4 = kotlin.js.Date(target.getFullYear(), 0, 4)
    val dayDiff = (target.getTime() - jan4.getTime()) / (1000.0 * 60 * 60 * 24)
    return (1 + (dayDiff / 7).toInt())
}

internal fun shortDate(dateStr: String): String {
    val d = parseDate(dateStr)
    return "${d.getDate()} ${MONTH_NAMES[d.getMonth()].take(3)}"
}

internal fun formatNiceDate(dateStr: String): String {
    val d = parseDate(dateStr)
    return "${DAY_NAMES_FULL[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}"
}

internal val DAY_NAMES_FULL = listOf("Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag")


// ──────── Time helpers ────────

internal fun parseHour(time: String): Int = time.split(":")[0].toInt()

internal fun parseMinutes(time: String): Int {
    val parts = time.split(":")
    return parts[0].toInt() * 60 + (parts.getOrNull(1)?.toInt() ?: 0)
}

internal fun minutesToTime(min: Int): String {
    val capped = min.coerceIn(0, 24 * 60)
    val h = (capped / 60).coerceAtMost(23)
    val m = capped % 60
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
}

internal fun snapMinutes(min: Int, snap: Int = 30): Int = (min / snap) * snap

// ──────── Slot lane assignment ────────

internal data class SlotLaneInfo(
    val slot: SlotDto,
    val lane: Int,
    val totalLanes: Int,
    val clusterSlots: List<SlotDto>
)

internal fun assignLanes(allSlots: List<SlotDto>, weekStart: String): List<SlotLaneInfo> {
    val result = mutableListOf<SlotLaneInfo>()
    val byDay = allSlots.groupBy { daysBetween(weekStart, it.day) }

    for ((_, daySlots) in byDay) {
        if (daySlots.isEmpty()) continue
        val n = daySlots.size
        val overlaps = Array(n) { mutableSetOf<Int>() }
        for (i in 0 until n) {
            for (j in i + 1 until n) {
                val aS = parseMinutes(daySlots[i].startTime)
                val aE = parseMinutes(daySlots[i].endTime)
                val bS = parseMinutes(daySlots[j].startTime)
                val bE = parseMinutes(daySlots[j].endTime)
                if (aS < bE && bS < aE) {
                    overlaps[i].add(j); overlaps[j].add(i)
                }
            }
        }
        val visited = BooleanArray(n)
        val clusterIds = IntArray(n) { -1 }
        var nextCluster = 0
        for (i in 0 until n) {
            if (visited[i]) continue
            val cid = nextCluster++
            val queue = ArrayDeque<Int>()
            queue.add(i); visited[i] = true
            while (queue.isNotEmpty()) {
                val cur = queue.removeFirst()
                clusterIds[cur] = cid
                for (nb in overlaps[cur]) {
                    if (!visited[nb]) { visited[nb] = true; queue.add(nb) }
                }
            }
        }
        val clusterGroups = daySlots.indices.groupBy { clusterIds[it] }
        for ((_, indices) in clusterGroups) {
            val sorted = indices.sortedWith(compareBy({ parseMinutes(daySlots[it].startTime) }, { parseMinutes(daySlots[it].endTime) }))
            val laneEnds = mutableListOf<Int>()
            val laneAssignments = mutableMapOf<Int, Int>()
            for (idx in sorted) {
                val start = parseMinutes(daySlots[idx].startTime)
                val end = parseMinutes(daySlots[idx].endTime)
                val available = laneEnds.indexOfFirst { it <= start }
                if (available >= 0) { laneEnds[available] = end; laneAssignments[idx] = available }
                else { laneAssignments[idx] = laneEnds.size; laneEnds.add(end) }
            }
            val totalLanes = laneEnds.size
            val clusterSlotList = indices.map { daySlots[it] }
            for (idx in indices) {
                result.add(SlotLaneInfo(daySlots[idx], laneAssignments[idx]!!, totalLanes, clusterSlotList))
            }
        }
    }
    return result
}

// ──────── Window auto-expand ────────

internal data class HourWindow(val startHour: Int, val endHour: Int)

internal fun computeHourWindow(
    workplaceStart: Int,
    workplaceEnd: Int,
    slotsInRange: List<SlotDto>
): HourWindow {
    var start = workplaceStart.coerceIn(0, 23)
    var end = workplaceEnd.coerceIn(1, 24)
    if (start >= end) { start = 0; end = 24 }
    slotsInRange.forEach { slot ->
        val sM = parseMinutes(slot.startTime)
        val eM = parseMinutes(slot.endTime)
        val sH = sM / 60
        val eH = (eM + 59) / 60
        if (sH < start) start = sH
        if (eH > end) end = eH
    }
    return HourWindow(start.coerceAtLeast(0), end.coerceAtMost(24))
}

// ──────── Style helpers ────────

internal fun org.jetbrains.compose.web.css.StyleScope.schemaModalLabel() {
    property("display", "block"); property("font-weight", "700"); property("font-size", "0.7rem")
    property("color", "var(--rp-muted)"); property("text-transform", "uppercase"); property("letter-spacing", "0.8px")
    property("margin-bottom", "0.4rem")
}

internal fun org.jetbrains.compose.web.css.StyleScope.schemaModalInput() {
    property("width", "100%"); property("padding", "0.7rem 0.85rem")
    property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "10px")
    property("font-size", "0.95rem"); property("font-weight", "600")
    property("outline", "none"); property("box-sizing", "border-box")
    property("background", "var(--rp-surface)"); property("color", "var(--rp-pine)")
    property("font-family", "'Manrope', system-ui, sans-serif")
}
